import { IconButton } from "../../primitives/icon-button";
import {
  Menu,
  MenuItem,
  MenuPopup,
  MenuRadioGroup,
  MenuRadioItem,
  MenuSubHeader,
  MenuTrigger,
} from "../../primitives/menu";
import { type Theme, useTheme } from "../../providers/theme-provider";

export type ProfileMenuLabels = {
  openMenuAriaLabel: string;
  engineeringOnly: string;
  appearance: string;
  matchBrowser: string;
  light: string;
  dark: string;
  currentArtifact: string;
  signOut: string;
};

export const DEFAULT_PROFILE_MENU_LABELS: ProfileMenuLabels = {
  openMenuAriaLabel: "Open account menu",
  engineeringOnly: "Engineering Only",
  appearance: "Theme",
  matchBrowser: "Match Browser",
  light: "Light",
  dark: "Dark",
  currentArtifact: "Current artifact",
  signOut: "Sign out",
};

export type ProfileMenuProps = {
  labels: ProfileMenuLabels;
  additionalContent?: React.ReactNode;
  engineeringOnly?: boolean;
  engineeringContent?: React.ReactNode;
  onOpenArtifact?: () => void;
  onSignOut: () => void;
};

const themeOptions = [
  { value: "system", label: "matchBrowser" },
  { value: "light", label: "light" },
  { value: "dark", label: "dark" },
] as const satisfies ReadonlyArray<{
  value: Theme;
  label: keyof Pick<ProfileMenuLabels, "matchBrowser" | "light" | "dark">;
}>;

export function ProfileMenu({
  additionalContent,
  labels,
  engineeringOnly = false,
  engineeringContent,
  onOpenArtifact,
  onSignOut,
}: ProfileMenuProps) {
  const { theme, setTheme } = useTheme();

  return (
    <Menu>
      <MenuTrigger
        render={
          <IconButton
            aria-label={labels.openMenuAriaLabel}
            icon="user"
            size="md"
            variant="outline"
          />
        }
      />
      <MenuPopup align="end" size="sm">
        {additionalContent}
        <MenuItem
          data-testid="nav-sign-out"
          leadingIcon="logout-exit"
          onClick={onSignOut}
          tone="destructive"
        >
          {labels.signOut}
        </MenuItem>
        {engineeringOnly ? (
          <>
            <MenuSubHeader>{labels.engineeringOnly}</MenuSubHeader>
            <MenuItem
              leadingIcon="settings-cog"
              submenu={
                <MenuRadioGroup
                  value={theme}
                  onValueChange={(value) => setTheme(value as Theme)}
                >
                  <MenuSubHeader>{labels.appearance}</MenuSubHeader>
                  {themeOptions.map(({ value, label }) => (
                    <MenuRadioItem key={value} value={value}>
                      {labels[label]}
                    </MenuRadioItem>
                  ))}
                </MenuRadioGroup>
              }
            >
              {labels.appearance}
            </MenuItem>
            {onOpenArtifact ? (
              <MenuItem leadingIcon="document" onClick={onOpenArtifact}>
                {labels.currentArtifact}
              </MenuItem>
            ) : null}
            {engineeringContent}
          </>
        ) : null}
      </MenuPopup>
    </Menu>
  );
}
